export type FontStyle = {
  id: string;
  label: string;
  sample: string;
  transform: (text: string) => string;
};

const SCRIPT: Record<string, string> = {
  a: "𝒶",
  b: "𝒷",
  c: "𝒸",
  d: "𝒹",
  e: "ℯ",
  f: "𝒻",
  g: "ℊ",
  h: "𝒽",
  i: "𝒾",
  j: "𝒿",
  k: "𝓀",
  l: "𝓁",
  m: "𝓂",
  n: "𝓃",
  o: "ℴ",
  p: "𝓅",
  q: "𝓆",
  r: "𝓇",
  s: "𝓈",
  t: "𝓉",
  u: "𝓊",
  v: "𝓋",
  w: "𝓌",
  x: "𝓍",
  y: "𝓎",
  z: "𝓏",
};

const GOTHIC: Record<string, string> = {
  a: "𝔞",
  b: "𝔟",
  c: "𝔠",
  d: "𝔡",
  e: "𝔢",
  f: "𝔣",
  g: "𝔤",
  h: "𝔥",
  i: "𝔦",
  j: "𝔧",
  k: "𝔨",
  l: "𝔩",
  m: "𝔪",
  n: "𝔫",
  o: "𝔬",
  p: "𝔭",
  q: "𝔮",
  r: "𝔯",
  s: "𝔰",
  t: "𝔱",
  u: "𝔲",
  v: "𝔳",
  w: "𝔴",
  x: "𝔵",
  y: "𝔶",
  z: "𝔷",
};

const DOUBLE: Record<string, string> = {
  a: "𝕒",
  b: "𝕓",
  c: "𝕔",
  d: "𝕕",
  e: "𝕖",
  f: "𝕗",
  g: "𝕘",
  h: "𝕙",
  i: "𝕚",
  j: "𝕛",
  k: "𝕜",
  l: "𝕝",
  m: "𝕞",
  n: "𝕟",
  o: "𝕠",
  p: "𝕡",
  q: "𝕢",
  r: "𝕣",
  s: "𝕤",
  t: "𝕥",
  u: "𝕦",
  v: "𝕧",
  w: "𝕨",
  x: "𝕩",
  y: "𝕪",
  z: "𝕫",
};

const SANS_BOLD: Record<string, string> = {
  a: "𝗮",
  b: "𝗯",
  c: "𝗰",
  d: "𝗱",
  e: "𝗲",
  f: "𝗳",
  g: "𝗴",
  h: "𝗵",
  i: "𝗶",
  j: "𝗷",
  k: "𝗸",
  l: "𝗹",
  m: "𝗺",
  n: "𝗻",
  o: "𝗼",
  p: "𝗽",
  q: "𝗾",
  r: "𝗿",
  s: "𝘀",
  t: "𝘁",
  u: "𝘂",
  v: "𝘃",
  w: "𝘄",
  x: "𝘅",
  y: "𝘆",
  z: "𝘇",
};

const MONO: Record<string, string> = {
  a: "𝚊",
  b: "𝚋",
  c: "𝚌",
  d: "𝚍",
  e: "𝚎",
  f: "𝚏",
  g: "𝚐",
  h: "𝚑",
  i: "𝚒",
  j: "𝚓",
  k: "𝚔",
  l: "𝚕",
  m: "𝚖",
  n: "𝚗",
  o: "𝚘",
  p: "𝚙",
  q: "𝚚",
  r: "𝚛",
  s: "𝚜",
  t: "𝚝",
  u: "𝚞",
  v: "𝚟",
  w: "𝚠",
  x: "𝚡",
  y: "𝚢",
  z: "𝚣",
};

const SMALL_CAPS: Record<string, string> = {
  a: "ᴀ",
  b: "ʙ",
  c: "ᴄ",
  d: "ᴅ",
  e: "ᴇ",
  f: "ғ",
  g: "ɢ",
  h: "ʜ",
  i: "ɪ",
  j: "ᴊ",
  k: "ᴋ",
  l: "ʟ",
  m: "ᴍ",
  n: "ɴ",
  o: "ᴏ",
  p: "ᴘ",
  q: "ǫ",
  r: "ʀ",
  s: "s",
  t: "ᴛ",
  u: "ᴜ",
  v: "ᴠ",
  w: "ᴡ",
  x: "x",
  y: "ʏ",
  z: "ᴢ",
};

const BUBBLE: Record<string, string> = {
  a: "ⓐ",
  b: "ⓑ",
  c: "ⓒ",
  d: "ⓓ",
  e: "ⓔ",
  f: "ⓕ",
  g: "ⓖ",
  h: "ⓗ",
  i: "ⓘ",
  j: "ⓙ",
  k: "ⓚ",
  l: "ⓛ",
  m: "ⓜ",
  n: "ⓝ",
  o: "ⓞ",
  p: "ⓟ",
  q: "ⓠ",
  r: "ⓡ",
  s: "ⓢ",
  t: "ⓣ",
  u: "ⓤ",
  v: "ⓥ",
  w: "ⓦ",
  x: "ⓧ",
  y: "ⓨ",
  z: "ⓩ",
};

const mapText = (text: string, map: Record<string, string>) => {
  return text
    .split("")
    .map((char) => map[char.toLowerCase()] ?? char)
    .join("");
};

const fullwidthText = (text: string) => {
  return text
    .split("")
    .map((char) =>
      /[a-z0-9]/i.test(char)
        ? String.fromCharCode(char.charCodeAt(0) + 65248)
        : char,
    )
    .join("");
};

const combineText = (text: string, mark: string) => {
  return text
    .split("")
    .map((char) => (char.trim() ? `${char}${mark}` : char))
    .join("");
};

const glitchText = (text: string) =>
  combineText(text, "\u035e\u035f\u033f\u0333");
const underlineText = (text: string) => combineText(text, "\u0332");
const strikeText = (text: string) => combineText(text, "\u0337");
const overlineText = (text: string) => combineText(text, "\u0305");

export const FONT_STYLES: FontStyle[] = [
  { id: "serif", label: "Serif", sample: "Serif", transform: (text) => text },
  {
    id: "script",
    label: "Script",
    sample: "𝒮𝒸𝓇𝒾𝓅𝓉",
    transform: (text) => mapText(text, SCRIPT),
  },
  {
    id: "gothic",
    label: "Gothic",
    sample: "𝔊𝔬𝔱𝔥𝔦𝔠",
    transform: (text) => mapText(text, GOTHIC),
  },
  {
    id: "double",
    label: "Double",
    sample: "𝔻𝕠𝕦𝕓𝕝𝕖",
    transform: (text) => mapText(text, DOUBLE),
  },
  {
    id: "sans",
    label: "Sans",
    sample: "𝗦𝗮𝗻𝘀",
    transform: (text) => mapText(text, SANS_BOLD),
  },
  {
    id: "mono",
    label: "Monospace",
    sample: "𝙼𝚘𝚗𝚘",
    transform: (text) => mapText(text, MONO),
  },
  {
    id: "smallcaps",
    label: "Small Caps",
    sample: "sᴍᴀʟʟ ᴄᴀᴘs",
    transform: (text) => mapText(text, SMALL_CAPS),
  },
  {
    id: "fullwidth",
    label: "Fullwidth",
    sample: "Ｆｕｌｌｗｉｄｔｈ",
    transform: fullwidthText,
  },
  {
    id: "bubble",
    label: "Bubble",
    sample: "ⓑⓤⓑⓑⓛⓔ",
    transform: (text) => mapText(text, BUBBLE),
  },
  {
    id: "underline",
    label: "Underline",
    sample: "u̲n̲d̲e̲r̲",
    transform: underlineText,
  },
  { id: "strike", label: "Strike", sample: "s̷t̷r̷i̷k̷e̷", transform: strikeText },
  {
    id: "overline",
    label: "Overline",
    sample: "o̅v̅e̅r̅",
    transform: overlineText,
  },
  { id: "glitch", label: "Glitch", sample: "g̳̿͟͞l̳̿͟͞i̳̿͟͞t̳̿͟͞c̳̿͟͞h̳̿͟͞", transform: glitchText },
];
